import { SupernovaException } from "./errors";

export type OmitStrict<T, K extends keyof T> = T extends any ? Pick<T, Exclude<keyof T, K>> : never;

export type Nullish<T> = T | null | undefined;

export type ExplicitPartial<T> = { [P in keyof T]: T[P] | undefined };

// Magic as far as I'm concerned.
// Taken from https://stackoverflow.com/a/50375286/3229534
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

// This utility lets T be indexed by any key
type Indexify<T> = T & { [str: string]: undefined };

// To make a type where all values are undefined, so that in AllUnionKeys<T>
// TS doesn't remove the keys whose values are incompatible, e.g. string & number
type UndefinedVals<T> = { [K in keyof T]: undefined };

// This returns a union of all keys present across all members of the union T
type AllUnionKeys<T> = keyof UnionToIntersection<UndefinedVals<T>>;

// Where the (rest of the) magic happens ✨
export type AllFields<T> = { [K in AllUnionKeys<T> & string]: Indexify<T>[K] };

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type Defined<T> = T extends null | undefined ? never : T;

// Makes specific properties in the type required
export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

type PickOptional<T> = {
  [P in keyof T as undefined extends T[P] ? P : never]: T[P];
};

type PickNotOptional<T> = {
  [P in keyof T as undefined extends T[P] ? never : P]: T[P];
};

export type OptionalToNullable<T> = {
  [K in keyof PickOptional<T>]-?: Exclude<T[K], undefined> | null;
} & {
  [K in keyof PickNotOptional<T>]: T[K];
};

export function forceUnwrapNullish<T>(value: Nullish<T>): T {
  if (value === null) throw new Error("Illegal null");
  if (value === undefined) throw new Error("Illegal undefined");
  return value;
}

export function trimLeadingSlash(string: string): string {
  return string.startsWith("/") ? string.substring(1) : string;
}

export function trimTrailingSlash(string: string): string {
  return string.endsWith("/") ? string.substring(0, string.length - 1) : string;
}

export function tryParseUrl(url: string): URL | null {
  try {
    return parseUrl(url);
  } catch (e) {
    console.error(`Error parsing URL ${url}`);
    console.error(e);
    return null;
  }
}

export function parseUrl(url: string): URL {
  return new URL(url.startsWith("https://") || url.startsWith("http://") ? url : `https://${url}`);
}

export function mapByUnique<V, K>(items: Iterable<V>, keyFn: (item: V) => K): Map<K, V> {
  const result = new Map<K, V>();
  for (const item of items) {
    result.set(keyFn(item), item);
  }
  return result;
}

export function groupBy<V, K>(items: V[], keyFn: (item: V) => K): Map<K, V[]> {
  const result = new Map<K, V[]>();

  for (const item of items) {
    const key = keyFn(item);
    const array = result.get(key);
    if (array) {
      array.push(item);
    } else {
      result.set(key, [item]);
    }
  }

  return result;
}

export function filterNonNullish<T>(items: Nullish<T>[]): T[] {
  return items.filter(Boolean) as T[];
}

export function nonNullishFilter<T>(item: Nullish<T>): item is T {
  return !!item;
}

export function nonNullFilter<T>(item: T | null): item is T {
  return item !== null;
}

export type ArrayElementType<T extends Default[], Default = any> = T extends (infer U)[] ? U : Default;

export function buildConstantEnum<Inferred extends ArrayElementType<Passed>[], Passed extends string[]>(
  values: Inferred
): { [key in Inferred[number]]: key } {
  const constMap = values.reduce((acc, code) => ({ ...acc, [code]: code }), {});

  return constMap as { [key in Inferred[number]]: key };
}

export async function promiseWithTimeout<T>(
  timeoutMs: number,
  promise: () => Promise<T>,
  onTimeout?: () => Error
): Promise<T> {
  let timeoutHandle: NodeJS.Timeout | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(onTimeout?.() ?? SupernovaException.timeout()), timeoutMs);
  });

  const result = await Promise.race([promise(), timeoutPromise]);

  clearTimeout(timeoutHandle);
  return result;
}

export async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function uniqueBy<K, V>(items: V[], prop: (v: V) => K): V[] {
  return Array.from(mapByUnique(items, prop).values());
}

export function areShallowObjectsEqual<T extends object>(lhs: T | undefined, rhs: T | undefined) {
  if ((lhs === undefined) !== (rhs === undefined)) return false;
  if (lhs === undefined || rhs === undefined) return true;

  if ((lhs === null) !== (rhs === null)) return false;
  if (lhs === null || rhs === null) return true;

  for (const key in lhs) {
    if (!(key in rhs) || lhs[key] !== rhs[key]) {
      return false;
    }
  }

  for (const key in rhs) {
    if (!(key in lhs)) {
      return false;
    }
  }

  return true;
}

export function isNotNullish<T>(value: T | null | undefined): value is T {
  return !isNullish(value);
}

export function isNullish<T>(value: T | null | undefined): value is null | undefined {
  return value === null || value === undefined;
}

export function hasProperty<K extends string>(prop: K) {
  return <T extends { [key in K]?: unknown }>(obj: T): obj is T & Required<Pick<T, K>> => {
    return obj[prop] !== null && obj[prop] !== undefined;
  };
}

export function chunkedArray<V>(array: V[], chunkSize: number): V[][] {
  const result: V[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
}

export function recordToMap<V>(record: Record<string, V>): Map<string, V> {
  const map = new Map<string, V>();
  for (const [k, v] of Object.entries(record)) map.set(k, v);
  return map;
}

/**
 * Applies direct properties from `update` to `object`.
 *
 *  - In case a property is not defined in `update`, it `object` will retain the current one
 *  - In case a property is null in `update`, it will be removed from `object`
 *
 * @param object base object to perform updates on
 * @param update source of property updates
 * @returns object by merging updates from `update` into `object`
 */
export function applyShallowObjectUpdate<T>(object: T, update: Partial<OptionalToNullable<T>>): T {
  const objectShallowCopy = { ...object };

  for (const [key, value] of Object.entries(update)) {
    if (value === null) {
      // @ts-expect-error it's very hard to get typescript to believe that the types will match
      objectShallowCopy[key] = undefined;
    } else if (value !== undefined) {
      // @ts-expect-error it's very hard to get typescript to believe that the types will match
      objectShallowCopy[key] = value;
    }
  }

  return objectShallowCopy;
}

export type Pagination = {
  skip?: number;
  take?: number;
};

export type DefinedProps<T> = {
  [K in keyof T as undefined extends T[K] ? never : K]: Exclude<T[K], undefined>;
};

/**
 * Returns a shallow copy of the input object with only properties that are not `undefined`.
 *
 * @param obj - The input object whose properties may include `undefined` values.
 * @returns A new object containing only the properties from the original object that have defined (non-undefined) values.
 */
export function pickDefined<T extends object>(obj: T): Partial<DefinedProps<T>> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined)) as Partial<DefinedProps<T>>;
}
